/**
 * Team email inbox: one group per campaign or topic, explicit priorities,
 * full queue.
 *
 * A draft filed into a campaign bundle groups under its campaign (linked to
 * the campaign console); every other draft groups by its topic. In the Open
 * view the queues render separately: the folded "Recently worked on" group
 * on top (copies of the drafts the owner acted on, newest action first,
 * finished ones behind "Show finished"; it stays when the inbox is empty),
 * "Back from snooze" (flat, each row keeps its campaign or topic), the main
 * queue grouped – a draft whose author works in requested changes keeps its
 * place there –, and a collapsed "Snoozed" group at the bottom. Campaign,
 * topic and priority filters apply to all of them.
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";

import { adminApi, type AdminReviewDoc, type ReviewListFilter } from "../../api/admin";
import { apiErrorDetail } from "../../api/client";
import { PageHeader } from "../../components/PageHeader";
import { Age } from "../../components/Age";
import { Pill } from "../../components/Pill";
import { ShortId } from "../../components/ShortId";
import { QueryBoundary } from "../../components/QueryBoundary";
import { ReviewGroup } from "../../components/review/ReviewGroup";
import { EmailDraftPriority, ReviewTriage } from "../../components/review/ReviewTriage";
import { SnoozeControl } from "../../components/review/SnoozeMenu";
import { SnoozedSection } from "../../components/review/SnoozedSection";
import { RecentlyWorkedSection } from "../../components/review/RecentlyWorkedSection";
import { useConfirm } from "../../components/confirm/context";
import { useToast } from "../../components/toast/context";
import { FilterChips } from "../../components/ui/FilterChips";
import { ShortcutHelp, ShortcutHint } from "../../components/ui/ShortcutHelp";
import { reviewStatusTone, reviewUrl } from "../../lib/aiTeam";
import { bundleUrl, campaignUrl } from "../../lib/console";
import {
  DRAFT_FILTERS, PRIORITY_LABELS, directSendMailbox, draftAgeSeconds, draftCampaign, draftReviewSearch,
  emailDraftFiles, emailOutcome, emailDraftFilters, groupEmailDrafts, matchesDraftFilters, signatureBlock,
} from "../../lib/emailDrafts";
import { useListNavigation, type ListRowProps } from "../../lib/hooks/useListNavigation";
import { toggled } from "../../lib/reviewGroups";
import { splitByQueue } from "../../lib/reviewQueue";
import { submissionTitle } from "../../lib/reviewVersions";
import {
  SEND_NEEDS_NO_COMMENTS, finishOutcome, finishQuestion, type EmailDelivery, type FinishContext,
} from "../../lib/reviewFinish";
import { formatReturnDate, snoozedUntilDate } from "../../lib/snooze";

export function EmailDraftsPage() {
  const [params, setParams] = useSearchParams();
  const { status, topic, campaign, priority } = emailDraftFilters(params);
  const draftsQuery = adminApi.aiTeam.reviews(status, "email");
  const recentQuery = adminApi.aiTeam.recentReviews("email");
  const topicsQuery = adminApi.aiTeam.reviewTopics();
  const finish = adminApi.aiTeam.finishReview();
  const snooze = adminApi.aiTeam.snoozeReview();
  const wake = adminApi.aiTeam.wakeReview();
  const setRecent = adminApi.aiTeam.setReviewRecent();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const sendMailboxes = adminApi.aiTeam.directSendMailboxes();
  // The mailbox "Approve & send" sends from – one of the owner's Exchange
  // mailboxes (`direct_send_eligible`); attachments go out with it.
  const sendFrom = (doc: AdminReviewDoc) => directSendMailbox(doc.email_meta, sendMailboxes.data);
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    next.set(key, value);
    setParams(next, { replace: true });
  };
  const detailUrl = (doc: AdminReviewDoc) =>
    `${reviewUrl(doc.id, "email")}${draftReviewSearch(doc, params.toString())}`;

  const approve = async (doc: AdminReviewDoc, delivery: EmailDelivery) => {
    const blocked = signatureBlock(doc.email_meta);
    if (blocked) {
      toast.show(blocked);
      return;
    }
    const ctx: FinishContext = {
      author: doc.agent_slug,
      isEmail: true,
      // Comments drafted on the detail page go along – and are worked in.
      feedbackCount: doc.draft_comment_count ?? 0,
      mailbox: sendFrom(doc) ?? doc.email_meta?.mailbox_address,
      attachments: doc.email_meta?.attachments ?? [],
      subject: doc.email_meta?.subject || doc.title,
    };
    if (!(await confirm(finishQuestion("approve", delivery, ctx)))) return;
    finish.mutate({ reviewId: doc.id, decision: "approve", delivery }, {
      onSuccess: (r) => {
        const outcome = finishOutcome(r, ctx);
        if (outcome.error) toast.error(outcome.text);
        else toast.ok(outcome.text);
      },
      onError: (error) => toast.error(`Approve failed: ${apiErrorDetail(error)}`),
    });
  };

  // The × takes a draft out of "Recently worked on"; Undo puts it back at
  // its earlier place.
  const removeRecent = (doc: AdminReviewDoc) => {
    const workedAt = doc.owner_worked_at ?? null;
    setRecent.mutate({ reviewId: doc.id, workedAt: null }, {
      onSuccess: () => toast.ok(`"${doc.email_meta?.subject || doc.title}" removed from Recently worked on`, {
        action: workedAt
          ? { label: "Undo", onClick: () => setRecent.mutate({ reviewId: doc.id, workedAt }, { onError: (e) => toast.error(e) }) }
          : undefined,
      }),
      onError: (e) => toast.error(e),
    });
  };

  const doSnooze = (doc: AdminReviewDoc, days: number) =>
    snooze.mutate({ reviewId: doc.id, days }, {
      onSuccess: (res) => toast.ok(res.snoozed_until
        ? `Snoozed until ${formatReturnDate(snoozedUntilDate(res.snoozed_until), Date.now())}`
        : "Snoozed"),
      onError: (e) => toast.error(e),
    });

  const topicOptions = new Map((topicsQuery.data ?? []).map((t) => [t.id, t.name]));
  for (const doc of draftsQuery.data ?? []) if (doc.topic) topicOptions.set(doc.topic.id, doc.topic.name);
  const campaignOptions = new Map<string, string>();
  for (const doc of draftsQuery.data ?? []) {
    const c = draftCampaign(doc);
    if (c) campaignOptions.set(c.slug, c.name);
  }
  // A campaign context outlives its last draft – keep it selectable.
  if (campaign !== "all" && campaign !== "none" && !campaignOptions.has(campaign)) campaignOptions.set(campaign, campaign);
  const visible = (draftsQuery.data ?? []).filter((doc) => matchesDraftFilters(doc, params));
  const queues = status === "open" ? splitByQueue(visible, { openAsMain: true }) : null;
  const groups = groupEmailDrafts(queues ? queues.main : visible);
  const recent = queues ? (recentQuery.data ?? []).filter((doc) => matchesDraftFilters(doc, params)) : [];
  const withAuthor = queues ? queues.main.filter((doc) => doc.status === "changes_requested").length : 0;
  // Keyboard order = visual order: returned rows, then unfolded groups.
  const navItems = [
    ...(queues?.returned ?? []),
    ...groups.filter((g) => !collapsed.has(g.id)).flatMap((g) => g.drafts),
  ];

  const nav = useListNavigation({
    items: navItems,
    getKey: (d) => d.id,
    onOpen: (d) => navigate(detailUrl(d)),
    // `a` approves the safe way: a draft, never a send.
    onApprove: (d) => void approve(d, "draft"),
    canApprove: (d) => d.status === "pending" && !finish.isPending,
    onSnooze: (d) => setMenuFor(d.id),
    canSnooze: (d) => d.status === "pending",
    enabled: menuFor === null,
  });

  const renderRow = (doc: AdminReviewDoc, showTopic = false) => (
    <DraftRow
      key={doc.id}
      doc={doc}
      href={detailUrl(doc)}
      showTopic={showTopic}
      active={nav.isActive(doc)}
      linkProps={nav.rowProps(doc)}
      canSend={!!sendFrom(doc)}
      approving={finish.isPending}
      onApprove={(delivery) => void approve(doc, delivery)}
      snoozing={snooze.isPending && snooze.variables?.reviewId === doc.id}
      menuOpen={menuFor === doc.id}
      onMenuOpenChange={(open) => setMenuFor(open ? doc.id : null)}
      onSnooze={(days) => doSnooze(doc, days)}
    />
  );

  return <>
    <PageHeader title="Email Drafts"
      description="Review emails by campaign and topic. P1 is urgent, P2 important, and P3 routine. Each author maintains their drafts; you can adjust the classification here." />
    <div className="flex items-center gap-3 mb-4 flex-wrap">
      <FilterChips
        aria-label="Draft status"
        options={DRAFT_FILTERS}
        value={status}
        onChange={(value: ReviewListFilter) => setFilter("status", value)}
      />
      <span className="ml-auto"><ShortcutHint /></span>
    </div>
    <div className="flex items-center gap-3 mb-5 flex-wrap">
      <label className="text-sm text-ink-low">Campaign <select className="input" aria-label="Filter by campaign" value={campaign} onChange={(e) => setFilter("campaign", e.target.value)}>
        <option value="all">All campaigns</option>
        <option value="none">No campaign</option>
        {[...campaignOptions].sort((a, b) => a[1].localeCompare(b[1])).map(([slug, name]) => <option key={slug} value={slug}>{name}</option>)}
      </select></label>
      <label className="text-sm text-ink-low">Topic <select className="input" aria-label="Filter by topic" value={topic} onChange={(e) => setFilter("topic", e.target.value)}>
        <option value="all">All topics</option>
        <option value="uncategorized">Uncategorized</option>
        {[...topicOptions].sort((a, b) => a[1].localeCompare(b[1])).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
      </select></label>
      <label className="text-sm text-ink-low">Priority <select className="input" aria-label="Filter by priority" value={priority} onChange={(e) => setFilter("priority", e.target.value)}>
        <option value="all">All priorities</option>
        {Object.entries(PRIORITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        <option value="unprioritized">Unprioritized</option>
      </select></label>
      {draftsQuery.data ? (
        <span className="text-sm text-ink-low tnum">
          {queues
            ? <>{queues.main.length - withAuthor} to review{withAuthor ? ` · ${withAuthor} with the author` : ""}{queues.returned.length ? ` · ${queues.returned.length} back` : ""}{queues.snoozed.length ? ` · ${queues.snoozed.length} snoozed` : ""}</>
            : <>{visible.length} of {draftsQuery.data.length} drafts</>}
        </span>
      ) : null}
    </div>
    <RecentlyWorkedSection
      className="mb-4"
      rows={recent}
      linkTo={detailUrl}
      title={(doc) => doc.email_meta?.subject || doc.title}
      detail={(doc) => `to: ${doc.email_meta?.to?.join(", ") || "—"}`}
      onRemove={removeRecent}
      removingId={setRecent.isPending ? setRecent.variables?.reviewId : null}
    />
    <QueryBoundary query={draftsQuery}>
      {() => {
        const nothing = !groups.length && !queues?.returned.length;
        if (nothing && !queues?.snoozed.length) {
          return <div className="empty-state"><div className="title">No drafts match this view</div><p className="hint">Choose another campaign, topic, priority, or status to view the rest of your inbox.</p></div>;
        }
        return <div className="space-y-4">
          {queues?.returned.length ? (
            <section className="panel panel--flush">
              <h2 className="queue-group-title">
                Back from snooze <span className="font-mono text-xs text-ink-low">{queues.returned.length}</span>
              </h2>
              <ul className="divide-y divide-rule border-t border-rule">{queues.returned.map((doc) => renderRow(doc, true))}</ul>
            </section>
          ) : null}
          {nothing ? (
            <div className="empty-state"><div className="title">Nothing to review</div><p className="hint">Everything pending in this view is snoozed – it comes back on its own.</p></div>
          ) : null}
          {groups.map((group) => {
            const waiting = group.drafts.filter((doc) => doc.status === "pending");
            return <ReviewGroup
              key={group.id}
              name={group.name}
              count={group.drafts.length}
              urgent={waiting.filter((doc) => doc.priority === 1).length}
              oldestWaitingSeconds={waiting.length ? Math.max(0, ...waiting.map(draftAgeSeconds)) : null}
              folded={collapsed.has(group.id)}
              onToggle={() => setCollapsed((current) => toggled(current, group.id))}
              action={group.campaign ? (
                <Link className="link-quiet text-xs px-4 shrink-0" to={campaignUrl(group.campaign.slug)}>campaign →</Link>
              ) : null}
            >
              {group.drafts.map((doc) => renderRow(doc))}
            </ReviewGroup>;
          })}
          {queues ? (
            <SnoozedSection
              rows={queues.snoozed}
              linkTo={detailUrl}
              title={(d) => d.email_meta?.subject || d.title}
              wakingId={wake.isPending ? wake.variables?.reviewId : null}
              onWake={(d) => wake.mutate({ reviewId: d.id }, {
                onSuccess: () => toast.ok(`"${d.email_meta?.subject || d.title}" is back`),
                onError: (e) => toast.error(e),
              })}
            />
          ) : null}
        </div>;
      }}
    </QueryBoundary>
    <ShortcutHelp open={nav.helpOpen} onClose={() => nav.setHelpOpen(false)} shortcuts={nav.shortcuts} />
  </>;
}

function DraftRow({
  doc, href, showTopic, active, linkProps, canSend, approving, onApprove,
  snoozing, menuOpen, onMenuOpenChange, onSnooze,
}: {
  doc: AdminReviewDoc;
  href: string;
  showTopic: boolean;
  active: boolean;
  linkProps: ListRowProps;
  /** The envelope names one of the owner's mailboxes the server sends from. */
  canSend: boolean;
  approving: boolean;
  onApprove: (delivery: EmailDelivery) => void;
  snoozing: boolean;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onSnooze: (days: number) => void;
}) {
  const pending = doc.status === "pending";
  const revising = doc.status === "changes_requested";
  const comments = doc.draft_comment_count ?? 0;
  const files = emailDraftFiles(doc.email_meta).length;
  const campaign = draftCampaign(doc);
  const [nowMs] = useState(() => Date.now());
  return <li className={clsx("px-4 py-3 space-y-2", active && "row-cursor")}>
    <div className="flex items-start gap-3">
      <Link className="min-w-0 flex-1" to={href} {...linkProps}>
        <span className="block text-sm text-ink font-medium">{doc.email_meta?.subject || doc.title}</span>
        <span className="block font-mono text-xs text-ink-low truncate">to: {doc.email_meta?.to?.join(", ") || "—"}</span>
      </Link>
      <EmailDraftPriority priority={doc.priority} />
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-mono text-xs text-ink-low">{doc.agent_slug}</span>
      <ShortId id={doc.id} />
      {campaign && doc.bundle ? (
        <Link className="link-quiet text-xs" to={bundleUrl(campaign.slug, doc.bundle.id)} title="Open the bundle in the campaign">
          {showTopic ? `${campaign.name} · ${doc.bundle.title}` : doc.bundle.title}
        </Link>
      ) : showTopic ? <span className="text-xs text-ink-low">{doc.topic?.name ?? "Uncategorized"}</span> : null}
      {doc.content_version > 1 ? <span className="text-xs text-ink-low" title={submissionTitle(doc)}>v{doc.content_version}</span> : null}
      {files ? <span className="text-xs text-ink-low">{files} {files === 1 ? "file" : "files"}</span> : null}
      {comments ? <span className="text-xs text-ink-low">{comments} {comments === 1 ? "comment" : "comments"}</span> : null}
      <Age seconds={draftAgeSeconds(doc)} title={`Waiting since v1 – ${submissionTitle(doc)}`} />
      {doc.queue === "snoozed" && doc.snoozed_until
        ? <Pill value={`snoozed · ${formatReturnDate(snoozedUntilDate(doc.snoozed_until), nowMs)}`} tone="muted" />
        : revising
          ? <Pill value="changes requested" tone="muted" />
          : <Pill value={emailOutcome(doc) ?? doc.status} tone={reviewStatusTone(doc.status)} />}
      <ReviewTriage doc={doc} />
      {pending ? (
        <span className="ml-auto flex items-center gap-2">
          <SnoozeControl doc={doc} onPick={onSnooze} disabled={snoozing} open={menuOpen} onOpenChange={onMenuOpenChange} />
          {signatureBlock(doc.email_meta) ? (
            <span className="text-xs text-warn-fg" title={signatureBlock(doc.email_meta) ?? undefined}>needs your signature</span>
          ) : (
            <>
              <button type="button" className="btn btn-sm btn-second" disabled={approving} onClick={() => onApprove("draft")}>Approve &amp; draft</button>
              {canSend ? (
                <button type="button" className="btn btn-sm btn-second" disabled={approving || comments > 0}
                  title={comments ? SEND_NEEDS_NO_COMMENTS : undefined} onClick={() => onApprove("send")}>Approve &amp; send</button>
              ) : null}
            </>
          )}
        </span>
      ) : revising ? (
        <span className="ml-auto text-xs text-ink-low">{doc.agent_slug ?? "the author"} revises</span>
      ) : null}
    </div>
  </li>;
}
